import type { ReactElement } from 'react'
import { ArrowDownIcon, ArrowUpIcon, ArrowsRightLeftIcon, MinusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import { cn } from 'app/lib/utils'
import type { FieldChange, ItemAnalysis } from './analysis'
import { FIELD_NAME, STAGE_NAME, type FieldFilter } from './MovementTable'
import { formatCost, formatTokens } from './pricing'
import { formatAccuracy, formatPoints } from './stats'

export type CallFilter = 'changed' | 'regressed' | 'improved' | 'all'

/** The calls a filter shows, in queue order; the comparison sheet steps through the same list. */
export function filterCalls(items: ItemAnalysis[], filter: CallFilter, field: FieldFilter | null): ItemAnalysis[] {
  return items.filter((item) => {
    if (
      field &&
      !item.changes.some(
        (change) => change.stage === field.stage && change.field === field.field && change.kind === field.kind,
      )
    ) {
      return false
    }
    if (filter === `changed`) return item.changes.length > 0
    if (filter === `regressed`) return item.status === `regressed`
    if (filter === `improved`) return item.status === `improved`
    return true
  })
}

const STATUS_ICON = {
  regressed: { Icon: ArrowDownIcon, className: `text-red-700`, label: `Worse` },
  improved: { Icon: ArrowUpIcon, className: `text-green-700`, label: `Better` },
  tied: { Icon: MinusIcon, className: `text-minimal`, label: `Same` },
} as const

export function changeLabel(change: Pick<FieldChange, 'field' | 'person'>): string {
  const name = FIELD_NAME[change.field] ?? change.field
  return change.person ? `${name} · person ${change.person}` : name
}

function ChangeChip({ change }: { change: FieldChange }): ReactElement {
  return (
    <span
      className={cn(
        `inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-caption-sm`,
        change.kind === `fixed`
          ? `border-green-200 bg-green-50 text-green-800`
          : `border-red-200 bg-red-50 text-red-800`,
      )}
      title={`Expected "${change.goldenValue}": baseline "${change.baselineValue}", candidate "${change.candidateValue}"`}
    >
      {change.kind === `fixed` ? `fixed` : `broke`} {changeLabel(change)}
    </span>
  )
}

export function CallQueue({
  items,
  visible,
  filter,
  onFilter,
  fieldFilter,
  onClearFieldFilter,
  onOpen,
  unstable,
}: {
  items: ItemAnalysis[]
  visible: ItemAnalysis[]
  filter: CallFilter
  onFilter: (filter: CallFilter) => void
  fieldFilter: FieldFilter | null
  onClearFieldFilter: () => void
  onOpen: (itemId: string) => void
  /** Calls that also changed in the latest noise check: their movement may be noise. */
  unstable: Set<string>
}): ReactElement {
  const count = (status: CallFilter) => filterCalls(items, status, null).length
  const options: SegmentedOption<CallFilter>[] = [
    {
      id: `changed`,
      label: `Changed ${count(`changed`)}`,
      title: `Calls where any field flipped, even if the score tied`,
    },
    { id: `regressed`, label: `Worse ${count(`regressed`)}` },
    { id: `improved`, label: `Better ${count(`improved`)}` },
    { id: `all`, label: `All ${items.length}` },
  ]
  const n = items.length

  return (
    <section aria-labelledby="calls-heading">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 id="calls-heading" className="text-sm font-semibold text-strong">
            Calls
          </h4>
          {fieldFilter && (
            <button
              type="button"
              onClick={onClearFieldFilter}
              className={cn(
                `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-caption-sm`,
                fieldFilter.kind === `fixed`
                  ? `border-green-200 bg-green-50 text-green-800`
                  : `border-red-200 bg-red-50 text-red-800`,
              )}
            >
              {STAGE_NAME[fieldFilter.stage]} · {FIELD_NAME[fieldFilter.field] ?? fieldFilter.field}
              {` `}
              {fieldFilter.kind}
              <XMarkIcon className="h-3.5 w-3.5" aria-label="Clear field filter" />
            </button>
          )}
        </div>
        <SegmentedControl options={options} value={filter} onSelect={onFilter} />
      </div>

      {visible.length === 0 ? (
        <p className="rounded-lg border border-dashed border-default p-6 text-center text-sm text-subtle">
          {filter === `changed` && !fieldFilter
            ? `No field flipped on any call: both prompts gave the same answers.`
            : `No calls match this filter.`}
        </p>
      ) : (
        <ul className="divide-y divide-[var(--line-subtle)] overflow-hidden rounded-lg border border-default bg-surface-raised">
          {visible.map((entry) => {
            const { Icon, className, label } =
              entry.status === `tied` && entry.changes.length > 0
                ? { Icon: ArrowsRightLeftIcon, className: `text-amber-700`, label: `Same score, fields moved` }
                : STATUS_ICON[entry.status]
            const shown = entry.changes.slice(0, 3)
            const result = entry.result
            return (
              <li key={result.itemId}>
                <button
                  type="button"
                  onClick={() => onOpen(result.itemId)}
                  className={cn(
                    `grid w-full grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 px-3 py-2.5 text-left transition-colors hover:bg-surface-subtle md:grid-cols-[auto_minmax(9rem,14rem)_1fr_auto] md:items-center`,
                    entry.status === `regressed` && `bg-red-50/40`,
                    entry.status === `improved` && `bg-green-50/40`,
                  )}
                >
                  <Icon className={cn(`h-4 w-4 md:row-auto`, className)} aria-label={label} />
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-strong">{result.callerName}</span>
                      {unstable.has(result.itemId) && (
                        <span
                          className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1 text-caption-sm text-amber-800"
                          title="This call's answer also changed between two runs of the same prompt"
                        >
                          unstable
                        </span>
                      )}
                    </div>
                    <div className="text-caption-sm tabular-nums text-subtle">
                      {formatAccuracy(result.baselineScore.overall, n)} →{' '}
                      {formatAccuracy(result.candidateScore.overall, n)}
                      {entry.delta !== 0 && (
                        <span className={cn(`ml-1.5`, entry.delta > 0 ? `text-green-700` : `text-red-700`)}>
                          {formatPoints(entry.delta, n)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-start-2 flex flex-wrap gap-1 md:col-start-auto">
                    {shown.map((change) => (
                      <ChangeChip key={`${change.stage}.${change.field}.${change.person ?? 0}`} change={change} />
                    ))}
                    {entry.changes.length > shown.length && (
                      <span className="text-caption-sm text-subtle">+{entry.changes.length - shown.length} more</span>
                    )}
                    {entry.changes.length === 0 && (
                      <span className="text-caption-sm text-minimal">No field flipped</span>
                    )}
                  </div>
                  <div className="col-start-2 text-caption-sm tabular-nums text-minimal md:col-start-auto md:text-right">
                    {formatTokens(result.tokens.promptTokens + result.tokens.completionTokens)} tok ·{` `}
                    {(result.latencyMs / 1000).toFixed(1)}s · {formatCost(result.cost)}
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
